import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CalendarClock, CheckCircle2, Download, FileText, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PaginationBar } from "@/components/PaginationBar";
import { usePagination } from "@/hooks/usePagination";
import { LeadService } from "@/services/lead.service";
import { AppointmentResultService } from "@/services/appointment-result.service";
import { supabase } from "@/lib/supabase";
import { isUuid } from "@/lib/uuid";
import { useAppData } from "@/data/AppDataContext";
import { OPPORTUNITY_STAGES, type OpportunityStage } from "@/data/opportunityTypes";
import type { DbLead } from "@/types/database";
import {
  leadAppointmentTemplateCsv,
  mapLeadAppointmentCsv,
  type LeadAppointmentImportRecord,
} from "@/data/import/leadAppointmentMapper";

type EditableLeadAppointmentField =
  | "customerName"
  | "phone"
  | "email"
  | "projectType"
  | "leadSource"
  | "leadCost"
  | "salesRep"
  | "receivedAt"
  | "acceptedAt"
  | "rejectedAt"
  | "stage";

type LeadSourceOption = {
  id: string;
  name: string;
};

type ExistingLeadForDuplicateCheck = Pick<
  DbLead,
  | "id"
  | "source_id"
  | "primary_name"
  | "primary_phone"
  | "primary_email"
  | "lead_source"
  | "lead_source_id"
  | "source_name"
  | "accepted_at"
  | "activities"
  | "stage_history"
>;

type DuplicateMatch = {
  reason: string;
  leadId?: string;
  leadName?: string;
};

type RowImportState = {
  status: "imported" | "failed";
  message?: string;
};

function downloadTextFile(filename: string, content: string) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function buildImportedLeadHistory(record: LeadAppointmentImportRecord) {
  const now = new Date().toISOString();
  const changedAt = record.acceptedAt || record.rejectedAt || record.closedAt || record.appointment?.scheduledAt || record.receivedAt || now;
  const activities = [
    {
      id: `import-activity-${record.externalId || Date.now()}`,
      type: "system" as const,
      description: `Imported lead as ${record.stage}${record.appointment ? ` with ${record.appointment.status} appointment` : ""}.`,
      createdAt: changedAt,
      createdBy: "Migration import",
    },
  ];

  if (record.notes) {
    activities.push({
      id: `import-note-${record.externalId || Date.now()}`,
      type: "note" as const,
      description: record.notes,
      createdAt: now,
      createdBy: "Migration import",
    });
  }

  return {
    stageHistory: [
      {
        id: `import-stage-${record.externalId || Date.now()}`,
        fromStage: null,
        toStage: record.stage,
        changedAt,
        changedBy: "Migration import",
      },
    ],
    activities,
  };
}

function normalizeLookupKey(value: string | null | undefined): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function resolveLeadSource(record: LeadAppointmentImportRecord, leadSources: LeadSourceOption[]) {
  const raw = record.leadSource.trim();
  if (!raw) return { id: null, name: "" };
  const normalized = normalizeLookupKey(raw);
  const match = leadSources.find((source) => source.id === raw || normalizeLookupKey(source.name) === normalized);
  return {
    id: match?.id ?? null,
    name: match?.name ?? raw,
  };
}

function toDateInputValue(value: string | undefined): string {
  if (!value) return "";
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10);
  return value;
}

function fromDateInputValue(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) return parsed.toISOString();
  return trimmed;
}

function normalizePhoneKey(value: string | null | undefined): string {
  return String(value ?? "").replace(/\D+/g, "");
}

function normalizeEmailKey(value: string | null | undefined): string {
  return String(value ?? "").trim().toLowerCase();
}

function normalizeDateKey(value: string | null | undefined): string {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return "";
  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10);
  return trimmed.slice(0, 10);
}

function rowKey(record: LeadAppointmentImportRecord, index: number): string {
  return `${index}:${record.externalId || record.customerName || "lead"}`;
}

function extractImportedExternalIds(value: unknown): string[] {
  if (!value) return [];
  if (typeof value === "string") {
    const matches = value.matchAll(/import-(?:activity|stage|note)-([^"\s,}]+)/g);
    return Array.from(matches, (match) => match[1]).filter(Boolean);
  }
  if (Array.isArray(value)) return value.flatMap(extractImportedExternalIds);
  if (typeof value === "object") {
    return Object.values(value as Record<string, unknown>).flatMap(extractImportedExternalIds);
  }
  return [];
}

function leadSourceNameForDuplicateCheck(lead: ExistingLeadForDuplicateCheck, leadSources: LeadSourceOption[]): string {
  const matchedSource = lead.lead_source_id
    ? leadSources.find((source) => source.id === lead.lead_source_id)?.name
    : "";
  return firstNonEmpty(matchedSource, lead.source_name, lead.lead_source);
}

function firstNonEmpty(...values: Array<string | null | undefined>): string {
  for (const value of values) {
    const trimmed = String(value ?? "").trim();
    if (trimmed) return trimmed;
  }
  return "";
}

function findDuplicateMatch(
  record: LeadAppointmentImportRecord,
  index: number,
  records: LeadAppointmentImportRecord[],
  existingLeads: ExistingLeadForDuplicateCheck[],
  leadSources: LeadSourceOption[],
): DuplicateMatch | null {
  const externalId = record.externalId.trim();
  const phone = normalizePhoneKey(record.phone);
  const email = normalizeEmailKey(record.email);
  const source = normalizeLookupKey(resolveLeadSource(record, leadSources).name || record.leadSource);
  const acceptedDate = normalizeDateKey(record.acceptedAt);

  if (externalId) {
    const duplicateLead = existingLeads.find((lead) => {
      if (normalizeLookupKey(lead.source_id) === normalizeLookupKey(externalId)) return true;
      const importedIds = new Set([
        ...extractImportedExternalIds(lead.activities),
        ...extractImportedExternalIds(lead.stage_history),
      ]);
      return importedIds.has(externalId);
    });
    if (duplicateLead) {
      return {
        reason: `External ID already imported (${externalId})`,
        leadId: duplicateLead.id,
        leadName: duplicateLead.primary_name,
      };
    }

    const duplicateRowIndex = records.findIndex((candidate, candidateIndex) =>
      candidateIndex !== index && candidateIndex < index && candidate.externalId.trim() === externalId,
    );
    if (duplicateRowIndex >= 0) {
      return { reason: `Duplicate external ID in CSV row ${duplicateRowIndex + 1}` };
    }
  }

  if ((phone || email) && source && acceptedDate) {
    const duplicateLead = existingLeads.find((lead) => {
      const leadSource = normalizeLookupKey(leadSourceNameForDuplicateCheck(lead, leadSources));
      const leadAcceptedDate = normalizeDateKey(lead.accepted_at);
      if (leadSource !== source || leadAcceptedDate !== acceptedDate) return false;
      const phoneMatches = phone && normalizePhoneKey(lead.primary_phone) === phone;
      const emailMatches = email && normalizeEmailKey(lead.primary_email) === email;
      return Boolean(phoneMatches || emailMatches);
    });
    if (duplicateLead) {
      return {
        reason: `Matching phone/email, source, and accepted date already exist`,
        leadId: duplicateLead.id,
        leadName: duplicateLead.primary_name,
      };
    }

    const duplicateRowIndex = records.findIndex((candidate, candidateIndex) => {
      if (candidateIndex === index || candidateIndex > index) return false;
      const candidatePhone = normalizePhoneKey(candidate.phone);
      const candidateEmail = normalizeEmailKey(candidate.email);
      const candidateSource = normalizeLookupKey(resolveLeadSource(candidate, leadSources).name || candidate.leadSource);
      const candidateAcceptedDate = normalizeDateKey(candidate.acceptedAt);
      if (candidateSource !== source || candidateAcceptedDate !== acceptedDate) return false;
      return Boolean((phone && candidatePhone === phone) || (email && candidateEmail === email));
    });
    if (duplicateRowIndex >= 0) {
      return { reason: `Duplicate phone/email + source + accepted date in CSV row ${duplicateRowIndex + 1}` };
    }
  }

  return null;
}

type ImportProfileRow = {
  id?: string | null;
  auth_user_id?: string | null;
  email?: string | null;
  full_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
};

function normalizeRepLookupKey(value: string | null | undefined): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function addRepLookupKey(map: Map<string, string | null>, key: string, profileId: string) {
  const normalized = normalizeRepLookupKey(key);
  if (!normalized || !profileId) return;
  const existing = map.get(normalized);
  if (existing && existing !== profileId) {
    map.set(normalized, null);
    return;
  }
  if (!map.has(normalized)) map.set(normalized, profileId);
}

async function resolveSalesRepIds(records: LeadAppointmentImportRecord[]): Promise<Map<string, string | null>> {
  const rawReps = Array.from(
    new Set(records.map((record) => record.salesRep.trim()).filter(Boolean)),
  );
  const resolved = new Map<string, string | null>();
  if (rawReps.length === 0) return resolved;

  const { data, error } = await supabase
    .from("profiles")
    .select("id, auth_user_id, email, full_name, first_name, last_name");

  if (error) throw error;

  const lookup = new Map<string, string | null>();
  for (const profile of (data ?? []) as ImportProfileRow[]) {
    const profileId = String(profile.id ?? "").trim();
    if (!profileId) continue;
    const firstLast = [profile.first_name, profile.last_name]
      .map((part) => String(part ?? "").trim())
      .filter(Boolean)
      .join(" ");

    addRepLookupKey(lookup, profileId, profileId);
    addRepLookupKey(lookup, String(profile.auth_user_id ?? ""), profileId);
    addRepLookupKey(lookup, String(profile.email ?? ""), profileId);
    addRepLookupKey(lookup, String(profile.full_name ?? ""), profileId);
    addRepLookupKey(lookup, firstLast, profileId);
  }

  for (const rawRep of rawReps) {
    const normalized = normalizeRepLookupKey(rawRep);
    const match = lookup.get(normalized);
    resolved.set(rawRep, match ?? (isUuid(rawRep) ? rawRep.trim() : null));
  }

  return resolved;
}

export default function LeadAppointmentImport() {
  const [rawCsv, setRawCsv] = useState("");
  const [csvFileName, setCsvFileName] = useState("");
  const [editableRecords, setEditableRecords] = useState<LeadAppointmentImportRecord[]>([]);
  const [leadSources, setLeadSources] = useState<LeadSourceOption[]>([]);
  const [existingLeads, setExistingLeads] = useState<ExistingLeadForDuplicateCheck[]>([]);
  const [isReadingFile, setIsReadingFile] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importingRowKey, setImportingRowKey] = useState<string | null>(null);
  const [rowImportStates, setRowImportStates] = useState<Record<string, RowImportState>>({});
  const [lastResult, setLastResult] = useState<{ leads: number; appointments: number; failures: string[] } | null>(null);
  const { refreshData } = useAppData();

  const parsedRecords = useMemo(() => {
    if (!rawCsv.trim()) return [];
    try {
      return mapLeadAppointmentCsv(rawCsv);
    } catch (err) {
      console.warn("Lead appointment CSV parse failed", err);
      return [];
    }
  }, [rawCsv]);
  const records = editableRecords;
  const duplicateMatches = useMemo(
    () => records.map((record, index) => findDuplicateMatch(record, index, records, existingLeads, leadSources)),
    [existingLeads, leadSources, records],
  );
  const previewPagination = usePagination(records, 10);
  const importableRowIndexes = useMemo(
    () => records
      .map((_, index) => index)
      .filter((index) => !duplicateMatches[index] && rowImportStates[rowKey(records[index], index)]?.status !== "imported"),
    [duplicateMatches, records, rowImportStates],
  );

  useEffect(() => {
    setEditableRecords(parsedRecords);
    setRowImportStates({});
    previewPagination.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedRecords]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [{ data, error }, leadsResult] = await Promise.all([
        supabase
          .from("lead_sources")
          .select("id, name")
          .order("name", { ascending: true }),
        LeadService.getAll({ forceRefresh: true }),
      ]);
      if (cancelled) return;
      if (error) {
        console.warn("Could not load lead sources for import preview", error);
      } else {
        setLeadSources(((data ?? []) as LeadSourceOption[]).filter((source) => source.id && source.name));
      }
      if (leadsResult.error) {
        console.warn("Could not load existing leads for duplicate checks", leadsResult.error);
      } else {
        setExistingLeads(((leadsResult.data ?? []) as ExistingLeadForDuplicateCheck[]).filter((lead) => lead.id));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const updateRecordField = (index: number, field: EditableLeadAppointmentField, value: string) => {
    setEditableRecords((current) => current.map((record, rowIndex) => {
      if (rowIndex !== index) return record;
      if (field === "stage") {
        return { ...record, stage: value as OpportunityStage };
      }
      if (field === "receivedAt" || field === "acceptedAt" || field === "rejectedAt") {
        return { ...record, [field]: fromDateInputValue(value) };
      }
      if (field === "leadCost") {
        const cleaned = value.replace(/[$,\s]/g, "");
        const parsed = cleaned ? Number(cleaned) : null;
        return { ...record, leadCost: Number.isFinite(parsed) ? parsed : null };
      }
      return { ...record, [field]: value };
    }));
    setLastResult(null);
    setRowImportStates((current) => {
      const next = { ...current };
      delete next[rowKey(records[index], index)];
      return next;
    });
  };

  const handleCsvFileUpload = async (file: File | null) => {
    if (!file) return;
    const lowerName = file.name.toLowerCase();
    const isCsvLike =
      lowerName.endsWith(".csv") ||
      file.type === "text/csv" ||
      file.type === "application/vnd.ms-excel" ||
      file.type === "";

    if (!isCsvLike) {
      toast.error("Please upload a CSV file.");
      return;
    }

    setIsReadingFile(true);
    try {
      const text = await file.text();
      setRawCsv(text);
      setCsvFileName(file.name);
      setLastResult(null);
      setRowImportStates({});
      toast.success(`Loaded ${file.name}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not read CSV file.");
    } finally {
      setIsReadingFile(false);
    }
  };

  const importRecords = async (rowIndexes?: number[]) => {
    if (records.length === 0) {
      toast.warning("Upload a lead/appointment CSV first.");
      return;
    }
    const targetIndexes = rowIndexes ?? importableRowIndexes;
    if (targetIndexes.length === 0) {
      toast.warning("No non-duplicate rows are ready to import.");
      return;
    }

    setIsImporting(true);
    if (targetIndexes.length === 1) setImportingRowKey(rowKey(records[targetIndexes[0]], targetIndexes[0]));
    const failures: string[] = [];
    let leadsCreated = 0;
    let appointmentsCreated = 0;

    try {
      const { data: authData } = await supabase.auth.getUser();
      const createdBy = authData.user?.id ?? "migration-import";
      const targetRecords = targetIndexes.map((index) => records[index]);
      const salesRepIds = await resolveSalesRepIds(targetRecords);

      for (const index of targetIndexes) {
        const record = records[index];
        const key = rowKey(record, index);
        try {
          const duplicateMatch = duplicateMatches[index];
          if (duplicateMatch) {
            throw new Error(duplicateMatch.reason);
          }

          const providedSalesRep = record.salesRep.trim();
          const assignedRepId = providedSalesRep ? salesRepIds.get(providedSalesRep) ?? null : undefined;
          if (providedSalesRep && !assignedRepId) {
            throw new Error(`Sales rep "${providedSalesRep}" was not found. Use a profile email, full name, profile UUID, or auth UUID.`);
          }

          const history = buildImportedLeadHistory(record);
          const leadSource = resolveLeadSource(record, leadSources);
          const leadCreate = await LeadService.create({
            primary_name: record.customerName,
            primary_phone: record.phone,
            primary_email: record.email || null,
            secondary_name: null,
            secondary_phone: "",
            secondary_email: null,
            address: record.address,
            city: record.city,
            state: record.state || "FL",
            zip: record.zip,
            project_type: record.projectType || "Other",
            source_id: record.externalId || null,
            lead_cost: record.leadCost,
            lead_source: leadSource.name || "migration",
            provider: "migration_import",
            lead_source_id: leadSource.id,
            stage: record.stage,
            appointment_time: record.appointment?.scheduledAt ?? null,
            assigned_rep: assignedRepId,
            received_at: record.receivedAt ?? null,
            accepted_at: record.acceptedAt ?? null,
            rejected_at: record.rejectedAt ?? null,
            ownership_history: [],
            stage_history: history.stageHistory,
            activities: history.activities,
            linked_quote_id: null,
            linked_job_id: null,
            notes: record.notes || null,
            branch_id: null,
          });

          const leadId = String((leadCreate.data as { id?: string } | null)?.id ?? "").trim();
          if (leadCreate.error || !leadId) {
            throw new Error(leadCreate.error instanceof Error ? leadCreate.error.message : String(leadCreate.error ?? "missing lead id"));
          }
          leadsCreated += 1;
          setRowImportStates((current) => ({
            ...current,
            [key]: { status: "imported", message: `Lead ${leadId} imported` },
          }));

          if (record.appointment) {
            const { error } = await AppointmentResultService.upsert({
              appointmentId: record.appointment.appointmentId,
              leadId,
              scheduledAt: record.appointment.scheduledAt,
              status: record.appointment.status,
              result: record.appointment.result,
              notes: record.notes || undefined,
              contactName: record.customerName,
              contactPhone: record.phone,
              contactEmail: record.email,
              createdBy,
              noSaleReason: record.lostReason,
              commissionEarnedAt: record.stage === "Closed Won" ? record.closedAt || record.appointment.scheduledAt : undefined,
            });
            if (error) throw new Error(error instanceof Error ? error.message : String(error));
            appointmentsCreated += 1;
          }
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          failures.push(`${record.customerName || record.externalId || "Unknown row"}: ${message}`);
          setRowImportStates((current) => ({
            ...current,
            [key]: { status: "failed", message },
          }));
        }
      }

      setLastResult({ leads: leadsCreated, appointments: appointmentsCreated, failures });
      await Promise.all([
        refreshData(),
        LeadService.getAll({ forceRefresh: true }).then((result) => {
          if (!result.error) {
            setExistingLeads(((result.data ?? []) as ExistingLeadForDuplicateCheck[]).filter((lead) => lead.id));
          }
        }),
      ]);
      if (failures.length > 0) {
        toast.warning(`Imported ${leadsCreated} lead(s) with ${failures.length} failure(s).`);
      } else {
        toast.success(`Imported ${leadsCreated} lead(s) and ${appointmentsCreated} appointment record(s).`);
      }
    } finally {
      setIsImporting(false);
      setImportingRowKey(null);
    }
  };

  return (
    <div className="min-h-screen bg-background p-6 text-foreground">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-muted-foreground">
              <CalendarClock className="h-4 w-4" /> Migration Import
            </div>
            <h1 className="mt-2 text-3xl font-bold">Lead & Appointment Import</h1>
            <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
              Separate importer for pipeline records. This creates leads and optional appointment results only; it does not create jobs.
            </p>
          </div>
          <Button variant="outline" className="gap-2" onClick={() => downloadTextFile("koredor-lead-appointment-template.csv", leadAppointmentTemplateCsv())}>
            <Download className="h-4 w-4" /> Download template
          </Button>
        </div>

        <Card className="space-y-4 p-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold">CSV file</h2>
              <p className="text-sm text-muted-foreground">
                Upload old-system lead data. Appointment columns are optional, so pure leads and closed lost/won records can import without fake appointments.
              </p>
            </div>
            <Button
              className="gap-2"
              disabled={isImporting || importableRowIndexes.length === 0}
              onClick={() => void importRecords()}
            >
              <Upload className="h-4 w-4" /> {isImporting ? "Importing..." : `Approve & import all ready (${importableRowIndexes.length})`}
            </Button>
          </div>

          <div className="rounded-lg border border-dashed bg-muted/20 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-md bg-background">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium">
                    {csvFileName || "Upload a lead/appointment CSV"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    The selected file is parsed into the editable preview below before import.
                  </p>
                </div>
              </div>
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm font-medium hover:bg-muted">
                <Upload className="h-4 w-4" />
                {isReadingFile ? "Reading..." : "Choose CSV"}
                <Input
                  type="file"
                  accept=".csv,text/csv"
                  className="sr-only"
                  disabled={isReadingFile || isImporting}
                  onChange={(event) => {
                    void handleCsvFileUpload(event.target.files?.[0] ?? null);
                    event.currentTarget.value = "";
                  }}
                />
              </label>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="mb-4 flex items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold">Preview</h2>
              <p className="text-sm text-muted-foreground">
                {records.length} lead row{records.length === 1 ? "" : "s"} parsed, {records.filter((record) => record.appointment).length} with appointment records.
                {" "}
                {duplicateMatches.filter(Boolean).length} duplicate{duplicateMatches.filter(Boolean).length === 1 ? "" : "s"} flagged.
              </p>
            </div>
            {lastResult && (
              <Badge variant={lastResult.failures.length ? "destructive" : "secondary"}>
                {lastResult.leads} leads / {lastResult.appointments} appointments imported
              </Badge>
            )}
          </div>
          {records.length === 0 ? (
            <div className="rounded-lg border border-dashed bg-muted/20 p-8 text-center text-sm text-muted-foreground">
              Upload a CSV to preview lead imports.
            </div>
          ) : (
            <div className="space-y-4">
              {previewPagination.paged.map((record, pageIndex) => {
                const index = previewPagination.pageStart - 1 + pageIndex;
                const key = rowKey(record, index);
                const sourceMatch = resolveLeadSource(record, leadSources);
                const sourceWarnings = record.leadSource && leadSources.length > 0 && !sourceMatch.id
                  ? [`Lead source "${record.leadSource}" has no master-data match`]
                  : [];
                const duplicateMatch = duplicateMatches[index];
                const rowState = rowImportStates[key];
                const warnings = [
                  ...record.warnings,
                  ...sourceWarnings,
                  ...(duplicateMatch ? [duplicateMatch.reason] : []),
                  ...(rowState?.status === "failed" && rowState.message ? [rowState.message] : []),
                ];
                return (
                  <div
                    key={key}
                    className={`rounded-xl border bg-muted/10 p-4 shadow-sm ${duplicateMatch ? "border-warning/50 bg-warning/5" : ""}`}
                  >
                    <div className="mb-4 flex flex-wrap items-start justify-between gap-3 border-b pb-3">
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                          Lead row {index + 1}
                        </div>
                        <div className="mt-1 text-lg font-semibold">{record.customerName || "Unnamed lead"}</div>
                        {record.externalId ? (
                          <div className="text-xs text-muted-foreground">External ID: {record.externalId}</div>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="outline">{record.stage}</Badge>
                        {rowState?.status === "imported" ? (
                          <Badge variant="secondary" className="gap-1 text-emerald-500">
                            <CheckCircle2 className="h-3 w-3" /> Imported
                          </Badge>
                        ) : null}
                        {duplicateMatch ? (
                          <Badge variant="outline" className="gap-1 border-warning/50 text-warning">
                            <AlertTriangle className="h-3 w-3" /> Duplicate
                          </Badge>
                        ) : null}
                        <Badge variant={record.appointment ? "secondary" : "outline"}>
                          {record.appointment ? "Has appointment" : "Lead only"}
                        </Badge>
                        <Button
                          size="sm"
                          className="gap-2"
                          disabled={isImporting || Boolean(duplicateMatch) || rowState?.status === "imported"}
                          onClick={() => void importRecords([index])}
                        >
                          <Upload className="h-3.5 w-3.5" />
                          {importingRowKey === key
                            ? "Importing..."
                            : rowState?.status === "imported"
                              ? "Imported"
                              : "Approve & import row"}
                        </Button>
                      </div>
                    </div>

                    <div className="grid gap-5 xl:grid-cols-[1.2fr_1fr_1fr]">
                      <section className="space-y-3">
                        <div className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                          Customer & Project
                        </div>
                        <label className="block space-y-1.5">
                          <span className="text-xs font-medium text-muted-foreground">Customer name</span>
                          <Input
                            value={record.customerName}
                            onChange={(event) => updateRecordField(index, "customerName", event.target.value)}
                          />
                        </label>
                        <div className="grid gap-3 sm:grid-cols-2">
                          <label className="block space-y-1.5">
                            <span className="text-xs font-medium text-muted-foreground">Phone</span>
                            <Input
                              value={record.phone}
                              placeholder="Primary phone"
                              onChange={(event) => updateRecordField(index, "phone", event.target.value)}
                            />
                          </label>
                          <label className="block space-y-1.5">
                            <span className="text-xs font-medium text-muted-foreground">Email</span>
                            <Input
                              value={record.email}
                              placeholder="Primary email"
                              onChange={(event) => updateRecordField(index, "email", event.target.value)}
                            />
                          </label>
                        </div>
                        <label className="block space-y-1.5">
                          <span className="text-xs font-medium text-muted-foreground">Project / install type</span>
                          <Input
                            value={record.projectType}
                            onChange={(event) => updateRecordField(index, "projectType", event.target.value)}
                          />
                        </label>
                      </section>

                      <section className="space-y-3">
                        <div className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                          Source & Assignment
                        </div>
                        <label className="block space-y-1.5">
                          <span className="text-xs font-medium text-muted-foreground">Lead source</span>
                          <Input
                            list="lead-appointment-import-source-options"
                            value={record.leadSource}
                            onChange={(event) => updateRecordField(index, "leadSource", event.target.value)}
                          />
                          <span className="block text-[11px] text-muted-foreground">
                            {sourceMatch.id ? "Matched to lead source master data." : "Text will be saved, but no source ID is matched."}
                          </span>
                        </label>
                        <label className="block space-y-1.5">
                          <span className="text-xs font-medium text-muted-foreground">Lead cost</span>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={record.leadCost ?? ""}
                            placeholder="0.00"
                            onChange={(event) => updateRecordField(index, "leadCost", event.target.value)}
                          />
                        </label>
                        <label className="block space-y-1.5">
                          <span className="text-xs font-medium text-muted-foreground">Sales rep</span>
                          <Input
                            value={record.salesRep}
                            placeholder="Email, name, profile UUID, or auth UUID"
                            onChange={(event) => updateRecordField(index, "salesRep", event.target.value)}
                          />
                          <span className="block text-[11px] text-muted-foreground">
                            Leave blank to assign to the current importer.
                          </span>
                        </label>
                        <label className="block space-y-1.5">
                          <span className="text-xs font-medium text-muted-foreground">Pipeline stage</span>
                          <select
                            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                            value={record.stage}
                            onChange={(event) => updateRecordField(index, "stage", event.target.value)}
                          >
                            {OPPORTUNITY_STAGES.map((stage) => (
                              <option key={stage} value={stage}>{stage}</option>
                            ))}
                          </select>
                        </label>
                      </section>

                      <section className="space-y-3">
                        <div className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                          Original Migration Dates
                        </div>
                        <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-1">
                          <label className="block space-y-1.5">
                            <span className="text-xs font-medium text-muted-foreground">Received at</span>
                            <Input
                              type="date"
                              value={toDateInputValue(record.receivedAt)}
                              onChange={(event) => updateRecordField(index, "receivedAt", event.target.value)}
                            />
                          </label>
                          <label className="block space-y-1.5">
                            <span className="text-xs font-medium text-muted-foreground">Accepted at</span>
                            <Input
                              type="date"
                              value={toDateInputValue(record.acceptedAt)}
                              onChange={(event) => updateRecordField(index, "acceptedAt", event.target.value)}
                            />
                          </label>
                          <label className="block space-y-1.5">
                            <span className="text-xs font-medium text-muted-foreground">Rejected at</span>
                            <Input
                              type="date"
                              value={toDateInputValue(record.rejectedAt)}
                              onChange={(event) => updateRecordField(index, "rejectedAt", event.target.value)}
                            />
                          </label>
                        </div>
                      </section>
                    </div>

                    <div className="mt-5 grid gap-4 border-t pt-4 lg:grid-cols-2">
                      <section className="rounded-lg border bg-background/60 p-3">
                        <div className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                          Appointment
                        </div>
                        <div className="mt-2 text-sm text-muted-foreground">
                          {record.appointment
                            ? `${record.appointment.status}${record.appointment.result ? ` / ${record.appointment.result}` : ""}`
                            : "No appointment will be created for this row."}
                        </div>
                      </section>
                      <section className="rounded-lg border bg-background/60 p-3">
                        <div className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                          Import checks
                        </div>
                        <div className="mt-2 text-sm text-muted-foreground">
                          {warnings.length ? warnings.join("; ") : "Ready to import."}
                          {duplicateMatch?.leadId ? (
                            <span className="block pt-1 text-xs text-warning">
                              Matched existing lead: {duplicateMatch.leadName || "Unknown"} ({duplicateMatch.leadId})
                            </span>
                          ) : null}
                        </div>
                      </section>
                    </div>
                  </div>
                );
              })}
              <PaginationBar {...previewPagination} />
            </div>
          )}
          <datalist id="lead-appointment-import-source-options">
            {leadSources.map((source) => (
              <option key={source.id} value={source.name} />
            ))}
          </datalist>
          {lastResult?.failures.length ? (
            <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              <div className="font-semibold">Import failures</div>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                {lastResult.failures.map((failure) => <li key={failure}>{failure}</li>)}
              </ul>
            </div>
          ) : null}
        </Card>
      </div>
    </div>
  );
}
